import React, { useEffect, useRef, useState } from 'react';
import { Star, ExternalLink, Quote } from 'lucide-react';
import { reviews } from '../data/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reviews = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [reviewStats, setReviewStats] = useState({ total_reviews: 273, average_rating: 4.6 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Fetch review stats from backend
    const fetchReviewStats = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings/reviews`);
        setReviewStats(response.data);
      } catch (error) {
        console.error('Error fetching review stats:', error);
      }
    };
    fetchReviewStats();
  }, []);

  // Calculate full and partial stars
  const fullStars = Math.floor(reviewStats.average_rating);
  const hasHalfStar = reviewStats.average_rating % 1 >= 0.3 && reviewStats.average_rating % 1 < 0.8;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <section id="avis" ref={sectionRef} className="py-24 bg-gradient-to-b from-black via-zinc-900 to-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl lg:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Ils en <span className="text-[#D4AF37]">parlent</span>
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
            Ce que nos clients disent de nous
          </p>

          {/* Google Rating */}
          <div className="inline-flex items-center space-x-4 bg-white/5 backdrop-blur-md p-6 rounded-2xl border border-[#D4AF37]/30">
            <div className="flex items-center space-x-1">
              {/* Full stars */}
              {[...Array(fullStars)].map((_, i) => (
                <Star
                  key={`full-${i}`}
                  size={32}
                  className="fill-[#D4AF37] text-[#D4AF37]"
                />
              ))}
              {/* Half star */}
              {hasHalfStar && (
                <div className="relative">
                  <Star size={32} className="text-white/30" />
                  <div className="absolute inset-0 overflow-hidden w-1/2">
                    <Star size={32} className="fill-[#D4AF37] text-[#D4AF37]" />
                  </div>
                </div>
              )}
              {/* Empty stars */}
              {[...Array(emptyStars)].map((_, i) => (
                <Star
                  key={`empty-${i}`}
                  size={32}
                  className="fill-white/30 text-white/30"
                />
              ))}
            </div>
            <div className="border-l-2 border-white/20 pl-4 text-left">
              <div className="text-3xl font-bold text-[#D4AF37]">{reviewStats.average_rating}/5</div>
              <div className="text-sm text-white/70">{reviewStats.total_reviews} avis Google</div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {reviews.map((review, index) => (
            <div
              key={index}
              className={`relative bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Quote Icon */}
              <Quote size={40} className="absolute top-4 right-4 text-[#D4AF37]/20 group-hover:text-[#D4AF37]/40 transition-colors" />
              
              {/* Stars */}
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} size={20} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>

              {/* Review Text */}
              <p className="text-white/90 text-lg leading-relaxed mb-4 relative z-10">"{review.text}"</p>

              {/* Author & Date */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="font-semibold text-[#D4AF37]">{review.author}</div>
                <div className="text-sm text-white/50">{review.date}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA to Google */}
        <div className="text-center">
          <a
            href="https://www.google.com/maps/place/Le+Cosmos+Saint+Etienne/@45.4387307,4.3943484,50m/data=!3m1!1e3!4m8!3m7!1s0x47f5afd8ed3b38f1:0x10b0411c8610ee0e!8m2!3d45.4386794!4d4.3943189!9m1!1b1!16s%2Fg%2F11h5v7kl8z?entry=ttu&g_ep=EgoyMDI2MDIwMS4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-[#D4AF37] text-black px-8 py-4 rounded-xl font-semibold hover:bg-[#FFD700] transition-all duration-200 hover:transform hover:-translate-y-1 hover:shadow-xl"
          >
            <span>Voir tous les avis sur Google</span>
            <ExternalLink size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
